import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Button,
  Alert,
  Avatar,
  Tag,
  Input,
  message
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhoneVolume,
  faBolt,
  faPhone,
  faUser,
  faCircleCheck,
  faSpinner,
  faVolumeHigh,
  faStop,
  faRightLeft
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import { API_BASE_URL } from '../../../constants/api';
import { VoiceCallModalContent, TranscriptContainer, VoiceCallButton } from '../styled';
import VoiceCallService from '../../../services/VoiceCallService';
import caseService from '../../../services/CaseService';
import SettingsService from '../../../services/SettingsService';
import MemberSelectionModal from './MemberSelectionModal';

const { Option } = Select;
const { TextArea } = Input;

const VoiceCallModal = ({ visible, onClose, patient, caseId, onCallComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [callState, setCallState] = useState('setup'); // setup, initiating, ringing, connected, ended
  const [callData, setCallData] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callNotes, setCallNotes] = useState('');
  const [callOutcome, setCallOutcome] = useState('completed'); // completed, no-answer, voicemail, wrong-number, disconnected
  const [loading, setLoading] = useState(false);
  const [statusPollInterval, setStatusPollInterval] = useState(null);

  // Active patient + editable phone (allows changing patient / fixing the number
  // before dialing) — initialized from the `patient` prop when the modal opens.
  const [activePatient, setActivePatient] = useState(patient || null);
  const [phoneNumber, setPhoneNumber] = useState(patient?.phone || '');
  const [memberPickerVisible, setMemberPickerVisible] = useState(false);
  
  // Real-time transcript state
  const [transcriptMessages, setTranscriptMessages] = useState([]);
  const [transcriptWs, setTranscriptWs] = useState(null);
  const [transcriptStats, setTranscriptStats] = useState({
    userMessages: 0,
    assistantMessages: 0,
    interruptions: 0
  });
  const [callStartTime, setCallStartTime] = useState(null);
  const [firstMessageTime, setFirstMessageTime] = useState(null);
  const transcriptEndRef = React.useRef(null);

  // Receptionist (assistant) display name, loaded from voice-call settings.
  const [receptionistName, setReceptionistName] = useState('Assistant');

  // Connect to real-time transcript WebSocket
  const connectTranscriptWebSocket = (callSid) => {
    // Derive the ws(s):// origin from the configured API base URL so this works
    // across local/dev/prod instead of a hardcoded localhost.
    const wsBase = API_BASE_URL.replace(/^http/, 'ws').replace(/\/api\/?$/, '');
    const wsUrl = `${wsBase}/api/voice-calls/transcript?callSid=${encodeURIComponent(callSid)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('📡 Connected to transcript stream for call:', callSid);
      // Record call start time when WebSocket connects
      setCallStartTime(new Date());
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'connected') {
        console.log('✅ Transcript stream connected:', data.message);
        return;
      }

      // Skip interim/partial transcript fragments — only render finalized turns
      // so the panel doesn't fill with duplicate, growing bubbles.
      if (data.partial) {
        return;
      }

      // Record first message time
      if (!firstMessageTime && (data.type === 'user_message' || data.type === 'assistant_message')) {
        setFirstMessageTime(new Date());
      }

      // Add message to transcript (conversation turns only)
      if (
        data.type === 'user_message' ||
        data.type === 'assistant_message' ||
        data.type === 'user_interruption'
      ) {
        let appended = false;
        setTranscriptMessages(prev => {
          // Vapi can deliver the same finalized turn more than once. Skip a
          // message that duplicates the last one of the same speaker/text.
          const last = prev[prev.length - 1];
          if (last && last.type === data.type && (last.text || '').trim() === (data.text || '').trim()) {
            return prev;
          }
          appended = true;
          return [...prev, data];
        });

        // Only count stats for messages we actually appended (not duplicates).
        if (appended) {
          if (data.type === 'user_message') {
            setTranscriptStats(prev => ({ ...prev, userMessages: prev.userMessages + 1 }));
          } else if (data.type === 'assistant_message') {
            setTranscriptStats(prev => ({ ...prev, assistantMessages: prev.assistantMessages + 1 }));
          } else if (data.type === 'user_interruption') {
            setTranscriptStats(prev => ({ ...prev, interruptions: prev.interruptions + 1 }));
          }
        }
      }

      // Auto-scroll to bottom
      setTimeout(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    };

    ws.onerror = (error) => {
      console.error('❌ Transcript WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('🔌 Transcript WebSocket closed');
    };

    setTranscriptWs(ws);
  };

  // Disconnect transcript WebSocket
  const disconnectTranscriptWebSocket = () => {
    if (transcriptWs) {
      transcriptWs.close();
      setTranscriptWs(null);
    }
  };

  // Poll for call status updates
  const pollCallStatus = async (callSid) => {
    try {
      const response = await VoiceCallService.getCallStatus(callSid);
      const { status, duration } = response.data;

      // Update call state based on backend status
      if (status === 'in-progress' && callState !== 'connected') {
        setCallState('connected');
        setCurrentStep(3);
        message.info('Patient answered - AI agent active');
        
        // Connect to transcript stream
        connectTranscriptWebSocket(callSid);
      } else if ((status === 'completed' || status === 'failed' || status === 'busy' || status === 'no-answer') && callState !== 'ended') {
        setCallState('ended');
        setCurrentStep(4);
        if (duration) {
          setCallDuration(duration);
        }
        message.info('Call ended');
        
        // Stop polling
        if (statusPollInterval) {
          clearInterval(statusPollInterval);
          setStatusPollInterval(null);
        }
        
        // Disconnect transcript stream
        disconnectTranscriptWebSocket();
      }
    } catch (error) {
      console.error('Error polling call status:', error);
    }
  };

  useEffect(() => {
    let interval;
    if (callState === 'connected') {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [callState]);

  // Load the configured receptionist name so the transcript labels the AI with
  // the same name it uses on the call (instead of a hardcoded placeholder).
  useEffect(() => {
    let active = true;
    SettingsService.getVoiceCallSettings()
      .then((res) => {
        const name = res?.data?.receptionistName?.trim();
        if (active && name) setReceptionistName(name);
      })
      .catch(() => { /* keep default label */ });
    return () => { active = false; };
  }, []);

  // Start status polling when call is initiated
  useEffect(() => {
    if (callData?.callSid && (callState === 'ringing' || callState === 'connected')) {
      const interval = setInterval(() => {
        pollCallStatus(callData.callSid);
      }, 2000); // Poll every 2 seconds

      setStatusPollInterval(interval);

      return () => {
        clearInterval(interval);
        setStatusPollInterval(null);
      };
    }
  }, [callData?.callSid, callState]);

  // Sync the active patient + editable phone from the prop each time the modal
  // opens (or the incoming patient changes).
  useEffect(() => {
    if (visible) {
      setActivePatient(patient || null);
      setPhoneNumber(patient?.phone || '');
    }
  }, [visible, patient]);

  useEffect(() => {
    if (!visible) {
      // Reset state when modal closes
      setCurrentStep(0);
      setCallState('setup');
      setCallData(null);
      setCallDuration(0);
      setCallNotes('');
      setTranscriptMessages([]);
      setTranscriptStats({ userMessages: 0, assistantMessages: 0, interruptions: 0 });
      setCallStartTime(null);
      setFirstMessageTime(null);
      form.resetFields();
      
      // Clear any active polling
      if (statusPollInterval) {
        clearInterval(statusPollInterval);
        setStatusPollInterval(null);
      }
      
      // Disconnect transcript WebSocket
      disconnectTranscriptWebSocket();
    }
  }, [visible, form, statusPollInterval]);

  // When a member is picked from the selector, swap the active patient + number.
  const handleSelectMember = (member) => {
    const mapped = {
      patientId: member.patientId,
      patientName: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unknown',
      phone: member.phoneNumber || member.phone || '',
      riskScore: member.riskScore,
      riskLevel: member.riskLevel,
      criticalFindings: member.criticalFindings,
      visitId: member.lastVisit?.visitId
    };
    setActivePatient(mapped);
    setPhoneNumber(mapped.phone || '');
    setMemberPickerVisible(false);
  };

  const handleInitiateCall = async () => {
    try {
      setLoading(true);
      setCurrentStep(1);
      setCallState('initiating');

      const values = form.getFieldsValue();
      
      // Start the voice call
      const response = await VoiceCallService.initiateVoiceCall({
        patientId: activePatient?.patientId,
        phoneNumber: phoneNumber,
        callType: 'results-discussion',
        priority: activePatient?.riskLevel === 'critical' || activePatient?.riskLevel === 'high' ? 'high' : 'medium',
        context: {
          visitId: activePatient?.visitId,
          riskScore: activePatient?.riskScore,
          riskLevel: activePatient?.riskLevel,
          criticalFindings: activePatient?.criticalFindings
        },
        metadata: {
          notes: values.initialNotes || '',
          initiatedBy: 'staff',
          initiatedAt: new Date().toISOString()
        }
      });

      setCallData(response.data);
      setCallState('ringing');
      setCurrentStep(2);
      
      message.success('Calling patient...');
      
      // Poll for call status updates
      pollCallStatus(response.data.callSid);

    } catch (error) {
      console.error('Failed to initiate call:', error);
      message.error('Failed to initiate call: ' + (error.message || 'Unknown error'));
      setCallState('setup');
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEndCall = async () => {
    try {
      if (callData?._id) {
        await VoiceCallService.endCall(callData._id);
        message.success('Call ended successfully');
      } else {
        console.warn('No call ID available to end call');
      }
      
      setCallState('ended');
      setCurrentStep(4);
      
      // Disconnect transcript stream
      disconnectTranscriptWebSocket();
    } catch (error) {
      console.error('Failed to end call:', error);
      message.error('Failed to end call: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSaveCallAndClose = async () => {
    try {
      // If we have a caseId, record the call attempt
      if (caseId) {
        await caseService.recordCallAttempt(caseId, {
          outcome: callOutcome,
          notes: callNotes,
          voiceCallId: callData?._id || callData?.callSid,
          duration: callDuration
        });
        
        message.success('Call recorded successfully');
        
        // Notify parent to refresh case data
        if (onCallComplete) {
          onCallComplete();
        }
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save call data:', error);
      message.error('Failed to save call data');
      // Still close modal even if save fails
      onClose();
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderSetupStep = () => {
    const riskLevel = activePatient?.riskLevel;
    const riskColor = riskLevel === 'critical' || riskLevel === 'high'
      ? '#ff4d4f'
      : riskLevel === 'medium'
        ? '#faad14'
        : COLORS.$primaryBlue;

    return (
      <div className="call-setup">
        {/* Patient hero */}
        <div className="setup-patient-card">
          <div className="setup-patient-main">
            <Avatar
              size={52}
              style={{ backgroundColor: activePatient ? riskColor : '#bfbfbf', flexShrink: 0 }}
              icon={<FontAwesomeIcon icon={faUser} />}
            />
            <div className="setup-patient-info">
              <div className="setup-patient-name">
                {activePatient?.patientName || 'No patient selected'}
              </div>
              <div className="setup-patient-sub">
                {phoneNumber ? (
                  <span className="setup-patient-phone">
                    <FontAwesomeIcon icon={faPhone} /> {phoneNumber}
                  </span>
                ) : (
                  <span className="setup-patient-muted">No phone number</span>
                )}
                {riskLevel && (
                  <Tag
                    color={riskColor}
                    style={{ fontWeight: 600, borderRadius: 6, margin: 0 }}
                  >
                    {riskLevel.toUpperCase()}
                    {activePatient?.riskScore != null && ` · ${activePatient.riskScore}`}
                  </Tag>
                )}
              </div>
            </div>
            <Button
              type={activePatient ? 'default' : 'primary'}
              icon={<FontAwesomeIcon icon={faRightLeft} />}
              onClick={() => setMemberPickerVisible(true)}
              style={{ borderRadius: 8, flexShrink: 0 }}
            >
              {activePatient ? 'Change' : 'Select'}
            </Button>
          </div>
        </div>

        {/* Call details form */}
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 18 }}
          autoComplete="off"
        >
          <Form.Item
            label="Phone Number"
            required
            validateStatus={!phoneNumber ? 'warning' : ''}
            help={!phoneNumber ? 'Enter a number to call' : 'You can edit the number before dialing'}
            style={{ marginBottom: 18 }}
          >
            <Input
              size="large"
              prefix={<FontAwesomeIcon icon={faPhone} style={{ color: COLORS.textMuted }} />}
              placeholder="+1 (555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="initialNotes"
            label="Call Notes (Optional)"
            style={{ marginBottom: 0 }}
          >
            <TextArea
              rows={3}
              placeholder="Add any notes or context for this call..."
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
        </Form>

        {/* AI agent note */}
        <div className="setup-agent-note">
          <FontAwesomeIcon icon={faBolt} style={{ color: COLORS.$primaryBlue }} />
          <span>An AI voice agent will place this call and discuss results using your Voice Settings.</span>
        </div>
      </div>
    );
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const getTopEmotions = (emotions) => {
    if (!emotions) return [];
    
    const emotionArray = Object.entries(emotions)
      .map(([name, score]) => ({ name, score }))
      .filter(emotion => emotion.score > 0.1) // Only show emotions > 10%
      .sort((a, b) => b.score - a.score)
      .slice(0, 3); // Top 3 emotions
    
    return emotionArray;
  };

  const calculateResponseTime = () => {
    if (callStartTime && firstMessageTime) {
      const diffMs = firstMessageTime - callStartTime;
      const seconds = (diffMs / 1000).toFixed(1);
      return seconds;
    }
    return null;
  };

  const renderTranscript = () => {
    const responseTime = calculateResponseTime();
    
    return (
      <TranscriptContainer>
        <div className="transcript-header">
          <div className="transcript-title">
            <FontAwesomeIcon icon={faVolumeHigh} />
            Live Conversation
          </div>
          <div className="transcript-stats">
            <div className="stat-item">
              <FontAwesomeIcon icon={faUser} />
              <span className="stat-value">{transcriptStats.userMessages}</span>
              <span>Patient</span>
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faBolt} />
              <span className="stat-value">{transcriptStats.assistantMessages}</span>
              <span>{receptionistName}</span>
            </div>
            <div className="stat-item">
              <FontAwesomeIcon icon={faStop} />
              <span className="stat-value">{transcriptStats.interruptions}</span>
              <span>Interruptions</span>
            </div>
          </div>
        </div>

        {responseTime && (
          <div className="response-time-banner">
            ⚡ First response in <span className="time-value">{responseTime}s</span>
          </div>
        )}

        <div className="transcript-messages">
          {transcriptMessages.length === 0 ? (
            <div className="transcript-empty">
              Waiting for conversation to start...
            </div>
          ) : (
            transcriptMessages.map((msg, index) => {
              if (msg.type === 'user_message') {
                const topEmotions = getTopEmotions(msg.emotions);
                return (
                  <div key={index} className="transcript-message user">
                    <div className="message-bubble">{msg.text}</div>
                    <div className="message-time">{formatTimestamp(msg.timestamp)}</div>
                    {topEmotions.length > 0 && (
                      <div className="message-emotions">
                        {topEmotions.map((emotion, i) => (
                          <span key={i} className="emotion-tag">
                            {emotion.name} ({Math.round(emotion.score * 100)}%)
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              } else if (msg.type === 'assistant_message') {
                return (
                  <div key={index} className="transcript-message assistant">
                    <div className="message-sender">
                      <FontAwesomeIcon icon={faBolt} style={{ marginRight: 4 }} />
                      {receptionistName}
                    </div>
                    <div className="message-bubble">{msg.text}</div>
                    <div className="message-time">{formatTimestamp(msg.timestamp)}</div>
                  </div>
                );
              } else if (msg.type === 'user_interruption') {
                return (
                  <div key={index} className="transcript-message interruption">
                    <FontAwesomeIcon icon={faStop} style={{ marginRight: 6 }} />
                    Patient interrupted - {formatTimestamp(msg.timestamp)}
                  </div>
                );
              }
              return null;
            })
          )}
          <div ref={transcriptEndRef} />
        </div>
      </TranscriptContainer>
    );
  };

  const renderCallProgressStep = () => {
    const stateMeta = {
      initiating: {
        label: 'Initiating Call',
        sub: `Setting up the voice agent and dialing ${phoneNumber}...`,
        color: COLORS.$primaryBlue,
        ring: 'pulse'
      },
      ringing: {
        label: 'Calling Patient',
        sub: `Ringing ${activePatient?.patientName || 'patient'} at ${phoneNumber}`,
        color: '#faad14',
        ring: 'pulse'
      },
      connected: {
        label: 'Call Connected',
        sub: 'AI agent is speaking with the patient',
        color: '#52c41a',
        ring: 'live'
      },
      ended: {
        label: 'Call Completed',
        sub: 'The call has ended',
        color: '#52c41a',
        ring: 'done'
      }
    };
    const meta = stateMeta[callState] || stateMeta.initiating;

    return (
      <div>
        <div className="call-hero">
          <div className={`call-hero-avatar ${meta.ring}`} style={{ '--ring-color': meta.color }}>
            <Avatar
              size={84}
              style={{ backgroundColor: meta.color }}
              icon={
                callState === 'ended'
                  ? <FontAwesomeIcon icon={faCircleCheck} />
                  : callState === 'connected'
                    ? <FontAwesomeIcon icon={faVolumeHigh} />
                    : <FontAwesomeIcon icon={faPhone} />
              }
            />
          </div>

          <div className="call-hero-status" style={{ color: meta.color }}>
            {callState === 'initiating' && <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />}
            {meta.label}
          </div>
          <div className="call-hero-sub">{meta.sub}</div>

          {(callState === 'connected' || callState === 'ended') && (
            <div className="call-duration-pill">
              <span className="dot" />
              {formatDuration(callDuration)}
            </div>
          )}
        </div>

        {callState === 'connected' && renderTranscript()}

        <div className="call-controls">
          {callState === 'connected' && (
            <VoiceCallButton
              type="primary"
              danger
              className="end-call"
              icon={<FontAwesomeIcon icon={faStop} />}
              onClick={handleEndCall}
            >
              End Call
            </VoiceCallButton>
          )}
        </div>
      </div>
    );
  };

  const renderCallSummaryStep = () => (
    <div>
      <Alert
        message="Call Completed"
        description={`Call with ${activePatient?.patientName || 'patient'} completed. Duration: ${formatDuration(callDuration)}`}
        type="success"
        icon={<FontAwesomeIcon icon={faCircleCheck} />}
        style={{ marginBottom: 20 }}
      />

      <Form layout="vertical">
        <Form.Item label="Call Outcome">
          <Select 
            value={callOutcome} 
            onChange={setCallOutcome}
            placeholder="Select call outcome"
          >
            <Option value="completed">Successfully Completed</Option>
            <Option value="no-answer">No Answer</Option>
            <Option value="voicemail">Left Voicemail</Option>
            <Option value="wrong-number">Wrong Number</Option>
            <Option value="disconnected">Call Disconnected</Option>
          </Select>
        </Form.Item>
        
        <Form.Item label="Call Notes">
          <TextArea
            rows={4}
            value={callNotes}
            onChange={(e) => setCallNotes(e.target.value)}
            placeholder="Add notes about what was discussed, patient's response, any follow-up needed..."
          />
        </Form.Item>
      </Form>
    </div>
  );

  const steps = [
    { content: renderSetupStep() },
    { content: renderCallProgressStep() },
    { content: renderCallProgressStep() },
    { content: renderCallProgressStep() },
    { content: renderCallSummaryStep() }
  ];

  const getModalFooter = () => {
    if (currentStep === 0) {
      return [
        <Button 
          key="cancel" 
          onClick={onClose}
          style={{ borderRadius: 8, padding: '8px 32px' }}
        >
          Cancel
        </Button>,
        <VoiceCallButton
          key="start"
          type="primary"
          className="start-call"
          icon={<FontAwesomeIcon icon={faPhoneVolume} />}
          onClick={handleInitiateCall}
          loading={loading}
          disabled={!activePatient || !phoneNumber}
          style={{ borderRadius: 8, padding: '8px 32px' }}
        >
          Start Voice Call
        </VoiceCallButton>
      ];
    }
    
    if (currentStep === 4) {
      return [
        <Button 
          key="close" 
          type="primary" 
          onClick={handleSaveCallAndClose}
          loading={loading}
          style={{ borderRadius: 8, padding: '8px 32px' }}
        >
          Save & Close
        </Button>
      ];
    }
    
    return null;
  };

  return (
    <>
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FontAwesomeIcon icon={faPhoneVolume} style={{ color: COLORS.$primaryBlue, fontSize: 20, marginRight: 8 }} />
          <span style={{ fontSize: 18, fontWeight: 600 }}>Voice Call Management</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: '800px' }}
      footer={getModalFooter()}
      destroyOnClose
      centered
      styles={{
        body: { padding: '24px' }
      }}
    >
      <VoiceCallModalContent>
        {steps[currentStep]?.content}
      </VoiceCallModalContent>
    </Modal>

    {/* Patient selector (change patient on the setup step) */}
    <MemberSelectionModal
      visible={memberPickerVisible}
      onClose={() => setMemberPickerVisible(false)}
      onSelectMember={handleSelectMember}
    />
    </>
  );
};

export default VoiceCallModal;

